import * as React from "react";
import { Select, Button, Form, FormInstance } from "antd";
import { observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { workflowDescriptionsList } from "Pages/Project/Workflow/Stores/WorkflowDescriptions";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { generatePath } from "react-router";
import { RouterStore } from "Config/CustomBrowserRouter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface StartWorkflowWithFilesProps extends WithTranslation, ModalComponentProps {
  fileIds?: API_FileDataEntity["id"][];
  startFrom?: API_WORKFLOWSTARTDEFINITION_STARTFROM;
}

interface StartWorkflowWithFilesState {
  disabledSubmit: string | boolean;
  loading: boolean;
  data: API_WorkflowListEntry[];
  attachedFiles: API_FileDataEntity["id"][];
}

// tslint:disable-next-line: max-line-length
export const StartWorkflowWithFiles = withTranslation(I18N_NS.WORKFLOW)(
  observer(
    class extends React.Component<StartWorkflowWithFilesProps, StartWorkflowWithFilesState> {
      constructor(props: StartWorkflowWithFilesProps) {
        super(props);
        this.state = {
          loading: false,
          disabledSubmit: this.WORKFLOW_DEFINITION_FIELD,
          data: [],
          attachedFiles: props.fileIds,
        };
      }
      WORKFLOW_DEFINITION_FIELD = "workflowdefinition";
      formRef: React.RefObject<FormInstance> = React.createRef();

      componentDidMount() {
        (async () => {
          try {
            this.setState({ loading: true });
            const projectId = projectsDataStore.projectId;
            const body: API_WorkflowStartDefinition = {
              attachedFiles: this.props.fileIds,
              startFrom: this.props.startFrom,
            };
            workflowDescriptionsList.setSelected(-1);
            const response: API_WorkflowListEntry[] = await API.newData(
              API_URL.WORKFLOWS.getWorkflowDefinitionsByFileIds(projectId),
              body
            );
            this.setState({ data: response });
          } catch (error) {
            throw error;
          } finally {
            this.setState({ loading: false });
          }
        })();
      }

      handleSubmit = async (values) => {
        const { t } = this.props;

        try {
          this.setState({ loading: true });
          const projectId = projectsDataStore.projectId;
          const body: API_WorkflowStartDefinition = {
            workflowDefinitionId: parseInt(`${values.workflowdefinition}`, 10),
            attachedFiles: this.props.fileIds,
          };
          // tslint:disable-next-line: max-line-length
          const response: API_ActionInfoRest = await API.newData(API_URL.WORKFLOWS.startWorkflow(projectId), body);
          if (!response.noStartInfo) {
            // show success message if "noStartInfo" flag is falsy
            if (
              response.status === "FINISHED" ||
              (response.status === "IN_PROGRESS" && response.worklistEntryId <= 0)
            ) {
              if (response.startInfo) {
                notificationSuccess(t(`Success`), response.startInfo);
              } else {
                notificationSuccess(t(`Workflow started`), values.name);
              }
            } else if (response.status === "IN_PROGRESS" && response.worklistEntryId > 0) {
              const workflowPath = generatePath("/project/:projectId/workflows/assignments/:worklistEntryId", {
                projectId: projectsDataStore.projectId.toString(),
                worklistEntryId: response.worklistEntryId.toString(),
              });
              RouterStore.push(workflowPath);
            }
          }
          if (typeof this.props.hideModal === "function") {
            this.props.hideModal();
          }
        } catch (err) {
          throw err;
        } finally {
          this.setState({ loading: false });
        }
      };

      onSelectChange = (value: string) => {
        workflowDescriptionsList.setSelected(parseInt(value, 10));
        console.log(this.formRef.current?.getFieldValue(this.WORKFLOW_DEFINITION_FIELD));
        this.setState({
          disabledSubmit:
            !this.formRef.current?.getFieldValue(this.WORKFLOW_DEFINITION_FIELD) ||
            this.state.attachedFiles?.length === this.selectedWorkflow.cantAccessFilesCounter,
        });
      };

      get selectedWorkflow() {
        return this.state.data.find((e) => e.workflowdefinition === workflowDescriptionsList._selected);
      }

      render() {
        const { t } = this.props;
        const { loading, data } = this.state;
        return (
          <>
            {loading || data.length ? (
              <Form disabled={loading} layout="inline" ref={this.formRef} onFinish={this.handleSubmit}>
                <Form.Item name={this.WORKFLOW_DEFINITION_FIELD}>
                  <Select
                    placeholder={t("Select a workflow")}
                    onChange={this.onSelectChange}
                    popupMatchSelectWidth={true}
                    style={{ minWidth: "24rem" }}
                    loading={loading}
                  >
                    {data.map((val: API_WorkflowListEntry) => {
                      return (
                        <Select.Option key={`${val.workflowdefinition}`} value={val.workflowdefinition.toString()}>
                          {val.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button
                    disabled={this.state.disabledSubmit as boolean}
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                  >
                    {t("Start workflow")}
                  </Button>
                </Form.Item>
              </Form>
            ) : (
              t("No workflows available.")
            )}
            {this.selectedWorkflow?.cantAccessFilesCounter > 0 && (
              <div>
                <FontAwesomeIcon
                  color="#ff8318"
                  className="mr-md pointer"
                  icon={["far", "exclamation-circle"]}
                  fixedWidth={true}
                  size="lg"
                />
                {t("This workflow will not be started for {{count}} of the selected files", {
                  count: this.selectedWorkflow.cantAccessFilesCounter,
                })}
              </div>
            )}
            {/* {selectedEntry && (
            <div>
              <h3>{selectedEntry.name}</h3>
              <p>{selectedEntry.description}</p>
            </div>
          )} */}
          </>
        );
      }
    }
  )
);
